import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { ELocalStorage } from '../enums/ELocalStorage';
import { BaseService } from './base-service';
import { AuthService } from './auth.service';
import { UtilsService } from './utils.service';
import { environment } from '../../environments/environment';
import { EModelApiPaths } from '../enums/EModelApiPaths';
import { ECollection } from '../enums/ECollection';
import { IUser, IUser_P } from '../interfaces/IUser';
import { EAuthTokenType, EGetEncryptedData, EHeader, IAPIResponse, IDecryptDataFETransfer, IDecryptDataFETransferPayload, IGetAllQueryParams, IQueryFormat } from '../enums/types';

@Injectable({
    providedIn: 'root'
})
export class UserService extends BaseService {
    amUser: { accessAMToken: '', refreshAMToken: '', accessAmDBToken: '' } | any;
    gUser = new Subject<any>();

    constructor(public override http: HttpClient,
                public authService: AuthService,
                public override utilsService: UtilsService,) {
        super(http, <any>null, utilsService, `/schema/${environment.USER_PATH}/${environment.INS}/${environment.DB}/${ECollection.LM_USERS}`);
        this.getThis().userService = this.userService;

        this.gUser.subscribe(async (data) => {
            if (!data) return;
            const tempUser = <any>this.authService.gUser;
            if (data.action === 'REFRESH_G_USER') {
                const userInfoRes = await this.loadGUserInfo(tempUser._id);
                this.authService.setUserToLocalStorage(userInfoRes[0], environment.crypto.passJWT);
            }
        });
    }

    async loadGUserInfo(id: string) {
        const findString = JSON.stringify({ _id: id });
        const resp = <any>await this.http.get(environment.PB_BE_HOST_PORT_SITES + `${this.modelPath}?find=` + findString, this.getRequestOptions()).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getAMToken() {
        const encryptedPayload = this.utilsService.getEncryptedString(<IDecryptDataFETransferPayload>{
            data: this.utilsService.authTokenAM,
            createdAt: new Date(),
        }, environment.crypto.passJWT);
        const res: IAPIResponse<IUser> = <any>await this.http.post(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.SYSTEM_API_BASE + `/${environment.USER_PATH}/token`,
            <IDecryptDataFETransfer>{ dataEncFE: encryptedPayload }, {
                headers: {
                    [EHeader.ENCRYPTED_PAYLOAD]: 'true',
                },
            }).toPromise();
        return res.data;
    }

    async getAMAdminAndRootToken(dataItems: any) {
        const encryptedPayload = this.utilsService.getEncryptedString(<IDecryptDataFETransferPayload>{
            data: dataItems,
            createdAt: new Date(),
        }, environment.crypto.passJWT);
        const res: IAPIResponse<IUser> = <any>await this.http.post(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.SYSTEM_API_BASE + `/${environment.USER_PATH}/token`,
            <IDecryptDataFETransfer>{ dataEncFE: encryptedPayload }, {
                headers: {
                    [EHeader.ENCRYPTED_PAYLOAD]: 'true',
                },
            }).toPromise();
        return res.data;
    }

    async login(usr_email_str: string, usr_pin_str: string) {
        const amToken = <any>await this.getAMToken();
        if (amToken) {
            const encryptedPayload = <IDecryptDataFETransfer>{
                dataEncFE: this.utilsService.getEncryptedString(<IDecryptDataFETransferPayload>{
                    data: {
                        usr_email_str,
                        usr_pin_str,
                    },
                    createdAt: new Date(),
                }, environment.crypto.passJWT),
            };
            const resp = <any>await this.http.post(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.USER_LOGIN, encryptedPayload, {
                headers: {
                    [EHeader.AUTHORIZATION_AM]: amToken.token,
                    [EHeader.ENCRYPTED_PAYLOAD]: 'true',
                    [EHeader.GET_ENCRYPTED_DATA]: EGetEncryptedData.get_only_encryption,
                },
            }).toPromise();
            const dataItem = this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
            this.setAMUserToLocalStorage({
                accessAMDBToken: dataItem.accessToken,
                refreshAMToken: dataItem.refresh_token,
                amToken: amToken,
            }, environment.crypto.passJWT);
            this.amUser.accessAMToken = amToken;
            this.amUser.accessAmDBToken = dataItem.accessToken;
            return dataItem;
        }
    }

    async register<T>(user: IUser) {
        const amToken = <any>await this.getAMToken();
        if (amToken) {
            const encryptedData = this.encryptedPayload(user);
            const resp: IAPIResponse<IUser> = <any>await this.http.post(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.USER_REGISTER, encryptedData, {
                headers: {
                    [EHeader.AUTHORIZATION_AM]: amToken.token,
                    [EHeader.ENCRYPTED_PAYLOAD]: environment.IS_ENCRYPTED ? 'true' : 'false',
                    [EHeader.GET_ENCRYPTED_DATA]: environment.IS_ENCRYPTED ? EGetEncryptedData.get_only_encryption : EGetEncryptedData.no_encryption,
                },
            }).toPromise();
            this.setAMUserToLocalStorage({
                accessAMToken: amToken.token,
                refreshAMToken: amToken.refresh_token,
            }, environment.crypto.passJWT);
            if (environment.IS_ENCRYPTED) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
            else return resp.data;
        }
    }

    async refreshToken() {
        if (this.authService.gUser && this.authService.gUser._id) {
            const authTokenAM = {
                authTokenType: EAuthTokenType.AM,
                authTokenAM: { refresh_token: this.amUser.refreshAMToken },
            };
            const authTokenAMDB = {
                authTokenType: EAuthTokenType.AM_DB,
                authTokenAMDB: { refresh_token: this.authService.token.refreshToken },
            };
            const res = <any>await this.http.post(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.SYSTEM_API_BASE + `/${environment.USER_PATH}/token`, [authTokenAM, authTokenAMDB]).toPromise();

            if (!res.data[0] || !res.data[1]) return this.authService.logout();

            // AM User
            const amUser = this.amUser;
            amUser.accessAMToken = <any>res.data[0].token;
            amUser.refreshAMToken = <any>res.data[0].refresh_token;
            this.setAMUserToLocalStorage(amUser, environment.crypto.passJWT);

            // AM_DB user
            const user: IUser = this.authService.gUser;
            this.authService.token.accessToken = res.data[1].token;
            this.authService.token.refreshToken = res.data[1].refresh_token;
            this.authService.setUserToLocalStorage(user, environment.crypto.passJWT);
            // await this.updateUser<IUser>(user._id, { lastLoginTime: new Date() }, {});
        }
    }

    setAMUserToLocalStorage(userObj: any, p: string) {
        this.amUser = userObj;
        const encryptedCode = this.utilsService.getEncryptedString(this.amUser, p);
        (<any>window).localStorage.setItem('AM_USER', encryptedCode);
    }

    getAMUserFromLocalStorage(p: string) {
        const value = (<any>window).localStorage.getItem('AM_USER');
        if (value) {
            const decryptedObj = this.utilsService.getDecryptedObject(value, p);
            if (decryptedObj) return decryptedObj;
            else (<any>window).localStorage.setItem('AM_USER', '');
        }
    }

    getRequestOptions() {
        let localData = this.getAMUserFromLocalStorage(environment.crypto.passJWT);
        if (localData) {
            return {
                headers: {
                    [EHeader.AUTHORIZATION_AM]: localData.amToken,
                    [EHeader.AUTHORIZATION_AM_USER]: localData.accessAMDBToken,
                    [EHeader.ENCRYPTED_PAYLOAD]: environment.IS_ENCRYPTED ? 'true' : 'false',
                    [EHeader.GET_ENCRYPTED_DATA]: environment.IS_ENCRYPTED
                        ? EGetEncryptedData.get_only_encryption : EGetEncryptedData.no_encryption,
                    [EHeader.SANDBOX_TIMEOUT]: (30 * (1000 * 60)).toString(), // 10 minutes
                },
            };
        } else {
            return {
                headers: {
                    [EHeader.AUTHORIZATION_AM]: window.localStorage.getItem(ELocalStorage.DEFAULT_AM_AUTH),
                    [EHeader.ENCRYPTED_PAYLOAD]: environment.IS_ENCRYPTED ? 'true' : 'false',
                    [EHeader.GET_ENCRYPTED_DATA]: environment.IS_ENCRYPTED
                        ? EGetEncryptedData.get_only_encryption : EGetEncryptedData.no_encryption,
                    [EHeader.SANDBOX_TIMEOUT]: (30 * (1000 * 60)).toString(), // 10 minutes
                },
            };
        }
    }

    async amUserQuery<T>(body: IQueryFormat, token?: string) {
        const encryptedData = this.encryptedPayload(body);
        const resp = <any>await this.http.post<T[]>(environment.PB_BE_HOST_PORT_SITES + `${this.modelPath}/query`, encryptedData, this.getRequestOptions()).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async saveUser<T>(body: T) {
        const encryptedData = this.encryptedPayload(body);
        const resp = <any>await this.http.post<T>(environment.PB_BE_HOST_PORT_SITES + `${this.modelPath}/save-single-or-multiple`, encryptedData, this.getRequestOptions()).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async updateUser<T>(id: string, body: Partial<T>, queryParams?: any) {
        const encryptedData = this.encryptedPayload(body);
        const resp = <any>await this.http.put<T>(environment.PB_BE_HOST_PORT_SITES + `${this.modelPath}/update-by-id/${id}`, encryptedData, {
            params: queryParams,
            headers: this.getRequestOptions().headers,
        }).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async permanentDeleteUser<T>(id: string) {
        const resp = <any>await this.http.delete<T>(environment.PB_BE_HOST_PORT_SITES + `${this.modelPath}/${id}`, this.getRequestOptions()).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getDistinctStatesAndCities<T>(body: any): Promise<T> {
        const resp = <any>await this.http.post<T>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.GET_DISTINCT_STATE_CITY_NAME, body,  this.getRequestOptions()).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getCourseNameCenterBatch<T>(body: any): Promise<T> {
        const resp = <any>await this.http.post<T>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.GET_COURSE_CENTER_BATCH_FOR_DROPDOWN, body,  this.getRequestOptions()).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async saveSingleMultipleUserWithoutToken<T>(body: any): Promise<T> {
        const resp = <any>await this.http.post<T>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.USER_REGISTER, body,  this.getRequestOptions()).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getCaptcha<T>(body: any): Promise<T> {
        const resp = <any>await this.http.post<T>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.CREATE_CAPTCHA, body,  this.getRequestOptions()).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async productFileUpload(body) {
        const encryptedData = this.encryptedPayload(body);
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.FILE_UPLOAD_PUBLIC, encryptedData,  this.getRequestOptions()).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async productFileDownload(body) {
        const encryptedData = this.encryptedPayload(body);
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + '/download-File', encryptedData, this.getRequestOptions()).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async removeDownload(body) {
        const encryptedData = this.encryptedPayload(body);
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + '/delete-File', encryptedData, this.getRequestOptions()).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }


    async checkIfEmailAlreadyExist(body) {
        const encryptedData = this.encryptedPayload(body);
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.CHECK_EMAIL_EXIST, encryptedData,  this.getRequestOptions()).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async sendEmailWithPin(body) {
        const encryptedData = this.encryptedPayload(body);
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.SEND_EMAIL_PIN, encryptedData,  this.getRequestOptions()).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async verifyEmailWithPin(body) {
        const encryptedData = this.encryptedPayload(body);
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.VERIFY_EMAIL_PIN, encryptedData,  this.getRequestOptions()).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getByIdWithSelectUser<T>(id: string, select: { [Property in keyof Partial<T>]: 1 | -1 }, deep: any): Promise<T> {
        const options: any = this.getRequestOptions();
        options.params = <IGetAllQueryParams>{
            deep: JSON.stringify(deep),
            select: JSON.stringify(select),
        };
        const resp = <any>await this.http.get<T>(environment.PB_BE_HOST_PORT_SITES + `${this.modelPath}/get-by-id/${id}`, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getByIdNew<T>(id: string): Promise<T> {
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.get<T>(environment.PB_BE_HOST_PORT_SITES + `${this.modelPath}/get-by-id/${id}`, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }


    async getStudentsForAdminDashBoard(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.ADMIN_DASHBOARD_GET_DATA_STUDENTS, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getCourseCreatorsForAdminDashBoard(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.ADMIN_DASHBOARD_GET_DATA_COURSE_CREATOR, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async studentApprovalCardsCounts(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.STUDENTS_APPROVAL_CARDS, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getCoursesCentersStudentsCountForSponsorDashboard(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.GET_COURSE_CENTER_STUDENT_COUNT_FOR_SPONSOR_DASHBOARD, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getCourseCardsSponsorDashboard(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.GET_COURSE_CARDS_SPONSOR_DASHBOARD, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getCenterCardsSponsorDashboard(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.GET_CENTER_CARDS_SPONSOR_DASHBOARD, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getCourseCardsDataForCCDashboard(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.GET_CC_DASHBOARD_COURSE_CARDS_DATA, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getCenterCardsDataForCCDashboard(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.GET_CC_DASHBOARD_CENTER_CARDS_DATA, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getStudentCardsDataForCCDashboard(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.GET_CC_DASHBOARD_STUDENT_CARDS_DATA, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getTrainerCountForAdminDashboard(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.GET_ADMIN_DASHBOARD_TRAINER_COUNT, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getCourseCreatorDashboardCount(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.GET_CC_DASHBOARD_COUNT, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async forgotPin(body) {
        const encryptedData = this.encryptedPayload(body);
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.FORGOT_PIN, encryptedData,  this.getRequestOptions()).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async recommendTrainerByCC(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.RECOMMEND_TRAINER_BY_CC, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async approvedTrainerByCC(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.APPROVE_TRAINER_BY_ADMIN, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getTrainerDashboardCourseCards(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.GET_TRAINER_DASHBOARD_COURSE_CARDS, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getTrainerDashboardCenterCards(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.GET_TRAINER_DASHBOARD_CENTER_CARDS, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async getTrainerDashboardCardsCount(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.GET_TRAINER_DASHBOARD_CARDS_COUNT, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

    async checkIfNewQuizAdded(body) {
        const encryptedData = this.encryptedPayload(body);
        const options: any = this.getRequestOptions();
        const resp = <any>await this.http.post<IAPIResponse<any>>(environment.PB_BE_HOST_PORT_SITES + EModelApiPaths.CUSTOM_API_BASE + `/${environment.USER_PATH}` + EModelApiPaths.CHECK_IF_NEW_QUIZ_ADDED, encryptedData, options).toPromise();
        if (this.isEncrypted) return this.utilsService.getDecryptedObject(resp.encryptedData, environment.crypto.passJWT);
        else return resp.data;
    }

}
